import React, { useState, useEffect, useCallback, memo, useMemo, useRef } from 'react';
import {
  Box,
  Typography,
  List,
  ListItemText,
  IconButton,
  TextField, // Keep TextField if still used directly (e.g., borehole name?)
  CircularProgress,
  Alert,
  Collapse,
  ListSubheader, // Keep if used for grouping/headers
  Divider,
  Paper, // Keep if used for main layout
  ListItemButton,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Tooltip, // Keep if Tooltip is used outside LayerListItem
  Autocomplete // Add Autocomplete here
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
// Removed LayersIcon, DeleteIcon as they are now in LayerListItem
import AddIcon from '@mui/icons-material/Add';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator'; // Add DragIndicatorIcon
import { supabase } from '../../supabaseClient';
import { useDomainValues } from '../Lookup_components/document_domainvalueslookup';
import { useCharacteristics } from '../Lookup_components/characteristic_lookup';
import debounce from 'lodash/debounce';
import GrassIcon from '@mui/icons-material/Straighten'; 
import LayerListItem from './LayerListItem'; // Import the new component
import { createCharacteristic, updateCharacteristic, deleteCharacteristic } from '../Lookup_components/characteristic_lookup'; // Ensure imports are correct
import { v4 as uuidv4 } from 'uuid'; // Import uuid if not already present
import Draggable from 'react-draggable'; // Add Draggable

// --- Draggable Dialog Component ---
function DraggableDialog(props) {
  const nodeRef = useRef(null);
  return (
    <Draggable nodeRef={nodeRef} handle="#draggable-dialog-title" cancel={'[class*="MuiDialogContent-root"]'}>
      <Paper ref={nodeRef} {...props} />
    </Draggable>
  );
}
// --------------------------------

// --- Copied AutocompleteField component --- 
// Standardized AutocompleteField (copied from LayerDetails.jsx)
const AutocompleteField = memo(({ 
  label, 
  value, // expects domain_value_id directly
  onChange, // expects function(newValueId)
  options, // expects array of { domain_value_id, description, ... }
  loading,
  keyPrefix,
  isReadOnly
}) => {
  const [open, setOpen] = useState(false);
  const [inputValue, setInputValue] = useState('');
  
  // Sort options alphabetically, except for unit fields
  const sortedOptions = useMemo(() => {
    if (!options || !Array.isArray(options)) return [];
    
    // Don't sort if the label contains "eenheid" (case insensitive)
    const isUnitField = label.toLowerCase().includes('eenheid');
    
    if (isUnitField) {
      return options;
    }
    
    return [...options].sort((a, b) => 
      (a.description || '').localeCompare(b.description || '')
    );
  }, [options, label]);

  const filteredOptions = useMemo(() => {
    if (!sortedOptions || !Array.isArray(sortedOptions)) return [];
    if (!inputValue || inputValue.length < 1) {
      return sortedOptions;
    }
    return sortedOptions.filter(option => 
      option.description?.toLowerCase().includes(inputValue.toLowerCase())
    );
  }, [inputValue, sortedOptions]);

  const currentOption = useMemo(() => {
    const found = (options || []).find(option => option.domain_value_id === value);
    return found || null;
  }, [options, value]);
  
  const handleInputChange = useCallback(debounce((event, newInputValue, reason) => {
    if (reason === 'input' || reason === 'clear') {
        setInputValue(newInputValue);
    } 
  }, 50), []);
  
  if (!options || !Array.isArray(options) || options.length === 0) { // Make check more robust
    return (
      <TextField
        fullWidth
        label={label}
        value=""
        disabled
        margin="normal"
        helperText={`Geen opties beschikbaar voor ${label}`}
      />
    );
  }

  return (
    <Autocomplete
      fullWidth
      open={open}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      options={filteredOptions} 
      getOptionLabel={(option) => option.description || ''} 
      isOptionEqualToValue={(option, val) => option.domain_value_id === val?.domain_value_id}
      value={currentOption} 
      onChange={(event, newValue) => {
        // Call the parent onChange with the new domain_value_id or null
        onChange(newValue?.domain_value_id || null); 
      }}
      renderOption={(liProps, option) => { 
        const { key, ...restLiProps } = liProps;
        return (
          <li
            key={key}
            {...restLiProps}
            style={{
              ...restLiProps.style,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              display: 'block'
            }}
          >
            {option.description || ''}
          </li>
        );
      }}
      onInputChange={handleInputChange} 
      renderInput={(params) => ( 
        <TextField 
          {...params} 
          label={label} 
          fullWidth
          margin="normal"
        />
      )}
      loading={loading && filteredOptions.length === 0} 
      loadingText="Opties laden..."
      noOptionsText="Geen resultaten"
    />
  );
}, (prevProps, nextProps) => {
  return (
    prevProps.value === nextProps.value &&
    prevProps.loading === nextProps.loading &&
    prevProps.options === nextProps.options 
  );
});
// -------------------------------------------

export default function LayerInfo({ document, isReadOnly }) {
  const [layers, setLayers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedBorehole, setExpandedBorehole] = useState(null);
  const [saving, setSaving] = useState(false);
  const [boreholes, setBoreholes] = useState([]);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [layerToDelete, setLayerToDelete] = useState(null);
  const [addingLayer, setAddingLayer] = useState(false);

  // --- NEW: State for Create Layer Dialog ---
  const [isCreateLayerDialogOpen, setIsCreateLayerDialogOpen] = useState(false);
  const [newLayerData, setNewLayerData] = useState(null);
  // -----------------------------------------

  // Fetch characteristics for the document
  const { characteristics: allCharacteristics, loading: characteristicsLoading, error: characteristicsError, setCharacteristics }
     = useCharacteristics({ document_id: document.id }); // Adjust based on what useCharacteristics returns

  // Define ALL technical names for domain values we need
  const technicalNames = useMemo(() => [ // Keep useMemo here for stability
    'Hoedanigheid',
    'BodemlaagType',
    'OrganischeStofGehalte',
    'GrindGehalte',
    'OliewaterReactieMate',
    'BodemsoortMediaan',
    'Bodemsoort',
    'Kleur',
    'Kleursterkte',
    'KenmerkBodemlaag',
    'Bemonsteringsapparaat',
    'Eenheid', // Add Eenheid here
    // --- NEW: Add technical names for Bijzonderheden options --- 
    'BodemlaagBijzonderheden',
    'BodemlaagBijzonderhedenGradatie'
  ], []);
  
  // Single call to useDomainValues for all names
  const { domainValues, loading: domainValuesLoading, error: domainValuesError } = useDomainValues(technicalNames);

  // Fetch specific units for length (similar to other tabs)
  const {
    domainValues: lengthUnitsData,
    loading: lengthUnitsLoading,
    error: lengthUnitsError
  } = useDomainValues({ technical_name: 'Eenheid', unit_dimension: 'l' });

  // Add new hook call for ReferentieVlak
  const {
    domainValues: referentieVlakData,
    loading: referentieVlakLoading,
    // error: referentieVlakError // Optional: handle error
  } = useDomainValues({ technical_name: 'Hoedanigheid', group_name: 'ReferentieVlak', sortByFrequency: true });

  // --- NEW: Find default IDs --- 
  const meterUnitId = useMemo(() => {
    const units = lengthUnitsData?.['Eenheid'];
    if (!units) return null;
    return units.find(unit => unit.description?.toLowerCase() === 'meter')?.domain_value_id || null;
  }, [lengthUnitsData]);

  const defaultConditionId = useMemo(() => {
    const conditions = referentieVlakData?.['Hoedanigheid'];
    if (!conditions) return null;
    return conditions.find(cond => cond.description?.toLowerCase() === 't.o.v. maaiveld')?.domain_value_id || null;
  }, [referentieVlakData]);

  // Fetch layers and related data on component mount or document change
  useEffect(() => {
    // Wait for IDs to be resolved before fetching layers
    if (document?.id && !lengthUnitsLoading && !referentieVlakLoading && meterUnitId !== undefined && defaultConditionId !== undefined) {
        fetchLayers(meterUnitId, defaultConditionId); // Pass IDs
    }
    // Fetch boreholes independently (doesn't depend on units/conditions)
    if (document?.id) {
        fetchBoreholes();
    }
  }, [document, lengthUnitsLoading, referentieVlakLoading, meterUnitId, defaultConditionId]); // Add ID dependencies

  const fetchLayers = async (meterUnitId, defaultConditionId) => { // Accept IDs
    setLoading(true); // Set loading at the start
    setError(null);
    try {
      const { data, error } = await supabase
        .from('document_layer')
        .select('*') // Select all fields needed by LayerListItem and LayerDetails
        .eq('document_id', document.id);

      if (error) throw error;

      let processedData = data || [];

      // --- NEW: Set default unit/condition if null and save to DB --- 
      if ((meterUnitId || defaultConditionId) && data && data.length > 0) {
        const updatePromises = [];
        processedData = data.map(layer => {
          let updates = {};
          let needsUpdate = false;
          let updatedLayer = { ...layer }; // Start with a copy

          if (meterUnitId) {
            if (layer.upperdepth_uom == null) { 
              updates.upperdepth_uom = meterUnitId; 
              updatedLayer.upperdepth_uom = meterUnitId; // Update local copy (ID is sufficient here)
              needsUpdate = true; 
            }
            if (layer.lowerdepth_uom == null) { 
              updates.lowerdepth_uom = meterUnitId; 
              updatedLayer.lowerdepth_uom = meterUnitId; 
              needsUpdate = true; 
            }
          }

          if (defaultConditionId) {
            if (layer.upperdepth_condition == null) { 
              updates.upperdepth_condition = defaultConditionId; 
              updatedLayer.upperdepth_condition = defaultConditionId; 
              needsUpdate = true; 
            }
            if (layer.lowerdepth_condition == null) { 
              updates.lowerdepth_condition = defaultConditionId; 
              updatedLayer.lowerdepth_condition = defaultConditionId; 
              needsUpdate = true; 
            }
          }

          if (needsUpdate) {
            updatePromises.push(
              supabase
                .from('document_layer')
                .update(updates)
                .eq('id', layer.id)
            );
          }
          return updatedLayer; // Return the potentially updated layer
        });

        // Wait for all background updates
        if (updatePromises.length > 0) {
          const results = await Promise.allSettled(updatePromises);
          let updateErrors = 0;
          results.forEach((result, index) => {
            if (result.status === 'rejected') {
              updateErrors++;
            }
          });
          if (updateErrors > 0) {
             setError(prev => {
                const errorMsg = `Kon voor ${updateErrors} laag/lagen de standaard waarde (eenheid/conditie) niet opslaan.`;
                return prev ? `${prev}\n${errorMsg}` : errorMsg;
             });
          }
        }
      }
      // --- END NEW --- 

      // Sort layers using natural sort based on upperdepth (use processedData)
      const sortedData = [...processedData].sort((a, b) => {
        // Als er geen bovendiepte is, zet deze onderaan
        if (a.upperdepth === null && b.upperdepth === null) return 0;
        if (a.upperdepth === null) return 1;
        if (b.upperdepth === null) return -1;
        
        // Sorteer op bovendiepte numeriek
        return a.upperdepth - b.upperdepth;
      });

      setLayers(sortedData); // Set state with potentially updated and sorted data
    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchBoreholes = async () => {
    try {
      const { data: projectData } = await supabase
        .from('document_project')
        .select('id')
        .eq('document_id', document.id)
        .single();

      if (projectData) {
        const { data, error } = await supabase
          .from('document_borehole')
          .select('id, name')
          .eq('project_id', projectData.id);
        
        if (error) throw error;
        setBoreholes(data || []);
      }
    } catch (error) {
      console.error('Error fetching boreholes:', error);
      // Potentially set error state here too
    }
  };

  // Keep validateDomainValue if needed by debouncedSave
  const validateDomainValue = (value, technicalName) => {
    if (!value) return true; // null/empty values are allowed
    const validValues = domainValues[technicalName] || [];
    return validValues.some(domain => domain.domain_value_id === value);
  };

  // Debounced save function remains here as it updates the parent state
  const debouncedSave = useCallback(
    debounce(async (updatedLayer) => {
      if (isReadOnly) return; // Don't save if read-only
      setSaving(true);
      try {
        // Validation logic remains here or could be moved to a separate utility function
        if (updatedLayer.upperdepth_uom !== undefined && updatedLayer.upperdepth_uom !== null) {
          if (!validateDomainValue(updatedLayer.upperdepth_uom, 'Eenheid')) {
            throw new Error('Ongeldige bovendiepte eenheid');
          }
        }
        if (updatedLayer.lowerdepth_uom !== undefined && updatedLayer.lowerdepth_uom !== null) {
          if (!validateDomainValue(updatedLayer.lowerdepth_uom, 'Eenheid')) {
            throw new Error('Ongeldige onderdiepte eenheid');
          }
        }
        if (updatedLayer.upperdepth_condition !== undefined && updatedLayer.upperdepth_condition !== null) {
          if (!validateDomainValue(updatedLayer.upperdepth_condition, 'Hoedanigheid')) {
            throw new Error('Ongeldige bovendiepte conditie');
          }
        }
        if (updatedLayer.lowerdepth_condition !== undefined && updatedLayer.lowerdepth_condition !== null) {
          if (!validateDomainValue(updatedLayer.lowerdepth_condition, 'Hoedanigheid')) {
            throw new Error('Ongeldige onderdiepte conditie');
          }
        }
        if (updatedLayer.layer_type !== undefined && updatedLayer.layer_type !== null) {
          if (!validateDomainValue(updatedLayer.layer_type, 'BodemlaagType')) {
            throw new Error('Ongeldig laagtype');
          }
        }

        // Clean undefined values to prevent database errors
        const cleanedLayer = Object.fromEntries(
          Object.entries(updatedLayer).filter(([_, value]) => value !== undefined)
        );

        const { error } = await supabase
          .from('document_layer')
          .update(cleanedLayer)
          .eq('id', updatedLayer.id);

        if (error) {
          throw error;
        }
      } catch (error) {
        console.error('Save error:', error);
        setError(`Fout bij opslaan: ${error.message}`);
        // Refresh data to ensure UI shows correct state
        await fetchLayers(); 
      } finally {
        setSaving(false);
      }
    }, 1000),
    // Update dependencies for debouncedSave
    [document.id, domainValues, isReadOnly] 
  );

  // Callback passed to LayerDetails via LayerListItem
  const handleFieldChange = useCallback(async (layer, fieldName, value) => {
    if (isReadOnly) return; // Don't change if read-only
    // Update local state immediately for smooth typing
    setLayers(prevLayers => 
      prevLayers.map(l => l.id === layer.id ? { ...l, [fieldName]: value } : l)
    );

    // Use debounced save for actual database update
    debouncedSave({ ...layer, [fieldName]: value });
  }, [debouncedSave, isReadOnly]);

  // Group layers by borehole (logic remains the same)
  const layersByBorehole = useMemo(() => {
    const grouped = {};
    
    boreholes.forEach(borehole => {
      grouped[borehole.id] = {
        borehole: borehole,
        layers: []
      };
    });
    
    layers.forEach(layer => {
      const boreholeId = layer.borehole_id;
      if (boreholeId && grouped[boreholeId]) { // Check if borehole exists in grouped
        grouped[boreholeId].layers.push(layer);
      } else {
        if (!grouped['unassigned']) {
          grouped['unassigned'] = {
            borehole: { id: 'unassigned', name: 'Niet toegewezen lagen' },
            layers: []
          };
        }
        grouped['unassigned'].layers.push(layer);
      }
    });

    // Sort boreholes alphabetically by name
    const sortedGrouped = {};
    Object.keys(grouped)
      .sort((a, b) => {
        // Keep unassigned at the bottom
        if (a === 'unassigned') return 1;
        if (b === 'unassigned') return -1;
        // Sort other boreholes by name
        return grouped[a].borehole.name.localeCompare(grouped[b].borehole.name, 'nl', { numeric: true });
      })
      .forEach(key => {
        sortedGrouped[key] = grouped[key];
      });

    // Sort layers within each borehole
    Object.values(sortedGrouped).forEach(group => {
      group.layers.sort((a, b) => {
        // Als er geen bovendiepte is, zet deze onderaan
        if (a.upperdepth === null && b.upperdepth === null) return 0;
        if (a.upperdepth === null) return 1;
        if (b.upperdepth === null) return -1;
        
        // Sorteer op bovendiepte numeriek
        return a.upperdepth - b.upperdepth;
      });
    });
    
    return sortedGrouped;
  }, [layers, boreholes]);

  // Handle layer deletion (triggered from LayerListItem)
  const handleDeleteLayer = useCallback((layer) => {
    setLayerToDelete(layer);
    setDeleteDialogOpen(true);
  }, []); // Empty dependency array as it only sets state based on arguments

  const confirmDeleteLayer = async () => {
    if (isReadOnly) { // Check isReadOnly
      setDeleteDialogOpen(false);
      setLayerToDelete(null);
      return;
    }
    setDeleteDialogOpen(false);
    
    if (!layerToDelete) return;
    
    try {
      // Show loading indicator specific to deletion?
      // setLoading(true); // Or use a different state variable
      
      // Delete the layer from the database
      const { error } = await supabase
        .from('document_layer')
        .delete()
        .eq('id', layerToDelete.id);
        
      if (error) throw error;
      
      // Update local state
      setLayers(prevLayers => prevLayers.filter(l => l.id !== layerToDelete.id));
      // No need to manage expandedItem here anymore
      
      // Check if characteristics were affected and need refresh
      // This check might be simplified or removed if characteristics handling is robust
      const linkedCharacteristics = allCharacteristics?.filter(c => c.link_id === layerToDelete.id);
      // Always refresh layers after delete to ensure consistency
      await fetchLayers(meterUnitId, defaultConditionId); // Refresh layers and pass IDs
      
    } catch (error) {
      console.error('Error deleting layer:', error);
      setError(`Fout bij verwijderen laag: ${error.message}`);
    } finally {
      // setLoading(false);
      setLayerToDelete(null);
    }
  };

  // Handle layer creation (now opens the dialog)
  const openNewLayerDialog = (boreholeId) => {
    if (isReadOnly) return; // Don't open if read-only
    setNewLayerData({
      document_id: document.id,
      borehole_id: boreholeId,
      upperdepth: 0,       // Default value
      lowerdepth: 0.5,     // Default value
      upperdepth_uom: meterUnitId,          
      lowerdepth_uom: meterUnitId,         
      upperdepth_condition: defaultConditionId, 
      lowerdepth_condition: defaultConditionId, 
      layer_type: null,     // Or find a sensible default?
      samplingdevice: null, // Or find a sensible default?
      remark: null,
    });
    setIsCreateLayerDialogOpen(true);
  };

  // --- UPDATED: Function to actually create the layer --- 
  const handleCreateLayer = async () => {
    if (isReadOnly || !newLayerData) return; // Check isReadOnly

    try {
      setAddingLayer(true); // Use existing addingLayer state for submit button
      setError(null);
      
      const newLayerId = uuidv4(); // Use uuidv4 import
      
      // Ensure numeric values are numbers or null
      const dataToInsert = {
        ...newLayerData,
        id: newLayerId,
        upperdepth: parseFloat(newLayerData.upperdepth) ?? null,
        lowerdepth: parseFloat(newLayerData.lowerdepth) ?? null,
      };

      // Validate essential fields (optional, but good practice)
      if (dataToInsert.upperdepth === null || dataToInsert.lowerdepth === null) {
         throw new Error("Boven- en onderdiepte zijn verplicht.");
      }
      
      const { error } = await supabase
        .from('document_layer')
        .insert(dataToInsert); // Insert prepared data
        
      if (error) throw error;
      
      // Update local state with the data we inserted
      setLayers(prevLayers => [...prevLayers, dataToInsert]);
      
      // Expand the borehole where the layer was added
      setExpandedBorehole(dataToInsert.borehole_id);

      // Close dialog and reset
      setIsCreateLayerDialogOpen(false);
      setNewLayerData(null); // Reset form data
      
    } catch (error) {
      console.error('Error adding layer:', error);
      setError(`Fout bij toevoegen laag: ${error.message}`);
    } finally {
      setAddingLayer(false); // Reset submit button state
    }
  };

  // --- NEW: Handler for dialog input changes --- 
  const handleNewLayerChange = (field, value) => {
    setNewLayerData(prev => ({
      ...prev,
      [field]: value
    }));
  };
  // ------------------------------------------

  // --- Mapping from technical name to indicator ID (based on provided image and context) ---
  const technicalNameToIndicatorId = useMemo(() => ({
      'OrganischeStofGehalte': 'cd31d615-0bb8-4e8c-a458-a7a2758e14f0', // Assuming Aandeel humus maps here
      'GrindGehalte': '176ebffd-8f6f-4c64-88ff-bc5499c42e7c',       // Assuming Aandeel grind maps here
      'Bodemsoort': '3fc46f47-67cf-47bd-8d37-13fa85ac45aa',
      'BodemsoortMediaan': 'dbc6d88b-d4a8-4ed7-ab04-639f06dce77a', // Mediaan
      'OliewaterReactieMate': 'b332f514-7138-4adb-b838-7d71d8d97776', // Oliewatermate
      'Kleursterkte': '1ee361c3-2ff8-45a1-a0e9-31892dfd52de',
      'KenmerkBodemlaag': '28d2ed28-f410-4db4-80fe-e576b61f04f4', // Laag kenmerk
      // NEW: Specific technical names for Kleur
      'Hoofdkleur': 'cb5ffc55-ec31-4416-b8be-a27bcab011a6',
      'Bijkleur': 'c245ce8e-2f4c-442a-b803-3438e83790b4',
      // --- NEW: Add Bijzonderheden ---
      'BodemlaagBijzonderheden': '52b97ef9-e18a-4135-a1b2-8dcfbbf81b39',
      'BodemlaagBijzonderhedenGradatie': 'ec52be58-4a33-426d-a18f-68bda1aae63c',
  }), []);

  // --- Mapping from technical name to BRO domain URN (example, verify with BRO specs) ---
  const technicalNameToDomain = useMemo(() => ({
      'OrganischeStofGehalte': 'urn:bro:domain:OrganischeStofGehalte',
      'GrindGehalte': 'urn:bro:domain:GrindGehalte',
      'Bodemsoort': 'urn:bro:domain:Bodemsoort',
      'BodemsoortMediaan': 'urn:bro:domain:KorrelgrootteMediaan', // Verify correct domain name
      // NEW: Specific domains for Kleur technical names
      'Hoofdkleur': 'urn:bro:domain:Kleur',
      'Bijkleur': 'urn:bro:domain:Kleur',
      'Kleursterkte': 'urn:bro:domain:Kleursterkte',
      'OliewaterReactieMate': 'urn:bro:domain:OliewaterReactieMate',
      'KenmerkBodemlaag': 'urn:bro:domain:KenmerkBodemlaag',
       // --- NEW: Add Bijzonderheden domains ---
      'BodemlaagBijzonderheden': 'immetingen:BodemlaagBijzonderheden', // Example, replace with actual domain if known
      'BodemlaagBijzonderhedenGradatie': 'immetingen:BodemlaagBijzonderhedenGradatie', // Example
  }), []);


  // --- UPDATED: Function to handle characteristic updates with LOGS and INDICATOR logic ---
  const handleCharacteristicUpdate = useCallback(async (layerId, documentId, fieldInfo, newValueId) => {
    if (isReadOnly) return; // Don't update if read-only
    // console.log('--- handleCharacteristicUpdate START ---');
    // console.log('Layer ID:', layerId);
    // console.log('Document ID:', documentId);
    // console.log('Field Info:', fieldInfo);
    // console.log('New Value ID:', newValueId);
    // console.log('Current allCharacteristics:', allCharacteristics);

    setSaving(true);
    setError(null);

    // FieldInfo now *always* contains technicalName
    const { technicalName, id: charInstanceIdFromFieldInfo } = fieldInfo;

    // Determine the indicatorId and domain based on the technicalName from the maps
    const indicatorToUse = technicalNameToIndicatorId[technicalName];
    const domainToUse = technicalNameToDomain[technicalName];

    // === NEW: Determine Hoofdkleur ID for Bijkleur/Kleursterkte ===
    let hoofdkleurId = null;
    const hoofdkleurIndicatorId = technicalNameToIndicatorId['Hoofdkleur']; 
    
    if (technicalName === 'Bijkleur' || technicalName === 'Kleursterkte') {
      const hoofdkleurChar = allCharacteristics.find(
        char => char.link_id === layerId && char.indicator === hoofdkleurIndicatorId
      );
      if (hoofdkleurChar) {
        hoofdkleurId = hoofdkleurChar.id;
        // console.log(`Found Hoofdkleur ID (${hoofdkleurId}) for layer ${layerId}`);
      } else {
        // console.warn(`Could not find Hoofdkleur for layer ${layerId} when updating ${technicalName}. Cannot set relatedObservation.`);
        // Optional: Prevent saving Bijkleur/Kleursterkte if Hoofdkleur is missing?
        // setError(`Kan ${technicalName} niet opslaan zonder een Hoofdkleur voor deze laag.`);
        // setSaving(false);
        // return; 
      }
    }
    // =============================================================

    // Validate that we found an indicator
    if (!indicatorToUse) {
        // console.error(`Error: Could not determine indicator ID for technicalName: ${technicalName}`);
        setError(`Fout: Kon indicator ID niet bepalen voor ${technicalName}.`);
        setSaving(false);
        return;
    }
    // console.log('Using Indicator ID:', indicatorToUse);

    // Validate domain (allow missing domain for Kleur, as it might not be directly needed for BRO export? TBC)
    if (!domainToUse && technicalName !== 'Hoofdkleur' && technicalName !== 'Bijkleur') {
        // console.warn(`Warning: Could not determine domain URN for technicalName: ${technicalName}`);
        // setError(`Fout: Kon domein URN niet bepalen voor ${technicalName}.`);
        // setSaving(false);
        // return;
    }


    try {
      // 1. Find existing characteristic based on link_id and the determined indicator
      // console.log(`Searching for existing char with link_id=${layerId} and indicator=${indicatorToUse}`);
      
      // MODIFIED: Prioritize finding by specific ID if provided
      let existingChar = null;
      if (charInstanceIdFromFieldInfo) {
        existingChar = allCharacteristics.find(char => char.id === charInstanceIdFromFieldInfo);
        // console.log(`Found existing char by specific ID (${charInstanceIdFromFieldInfo}):`, existingChar);
      } else {
        existingChar = allCharacteristics.find(
          char => char.link_id === layerId && char.indicator === indicatorToUse
        );
        // console.log(`Found existing char by layerId/indicator:`, existingChar);
      }
      // END MODIFIED
      
      // console.log('Found existing characteristic:', existingChar);

      // 2. Determine action: create, update, or delete
      if (existingChar) {
        if (newValueId === null || newValueId === undefined || newValueId === '') { // Also treat empty string as delete
          // Delete characteristic
          // console.log(`ACTION: Deleting characteristic ${existingChar.id}`);
          await deleteCharacteristic(existingChar.id);
          // console.log(`Characteristic ${existingChar.id} deleted from DB.`);
          // Update local state
          setCharacteristics(prev => {
              const newState = prev.filter(c => c.id !== existingChar.id);
              // console.log('Local state after delete:', newState);
              return newState;
          });
          
          // === NEW: Handle Hoofdkleur deletion - Clear relatedObservation for Bijkleur/Kleursterkte ===
          if (technicalName === 'Hoofdkleur') {
              const relatedChars = allCharacteristics.filter(
                  char => char.link_id === layerId && char.relatedObservation === existingChar.id
              );
              // console.log(`Found ${relatedChars.length} characteristics related to deleted Hoofdkleur ${existingChar.id}`);
              for (const relatedChar of relatedChars) {
                   // console.log(`Clearing relatedObservation for characteristic ${relatedChar.id}`);
                  try {
                      const updatedRelatedChar = await updateCharacteristic(relatedChar.id, { relatedObservation: null });
                      // Update local state for the related char
                      setCharacteristics(prev => prev.map(c => c.id === relatedChar.id ? { ...c, ...updatedRelatedChar } : c));
                      // console.log(`Cleared relatedObservation for ${relatedChar.id} in DB and state.`);
                  } catch(updateError) {
                      console.error(`Failed to clear relatedObservation for characteristic ${relatedChar.id}:`, updateError);
                      // Continue trying to update others, but report error
                      setError(`Fout bij bijwerken gerelateerde kleur ${relatedChar.id}: ${updateError.message}`);
                  }
              }
          }
          // =============================================================================================

        } else if (existingChar.classifiedResult !== newValueId || 
                   ((technicalName === 'Bijkleur' || technicalName === 'Kleursterkte') && existingChar.relatedObservation !== hoofdkleurId) ) { // NEW: Check if relatedObservation needs update
          // Update characteristic
          // console.log(`ACTION: Updating characteristic ${existingChar.id} with value ${newValueId}`);
          
          // === NEW: Include relatedObservation in updates for Bijkleur/Kleursterkte ===
          const updateData = {
            classifiedResult: newValueId,
            resulttime: new Date().toISOString() // Update result time
          };
          if (technicalName === 'Bijkleur' || technicalName === 'Kleursterkte') {
              updateData.relatedObservation = hoofdkleurId; // Add/update the link
          }
          // ===========================================================================

          const updatedChar = await updateCharacteristic(existingChar.id, updateData);
          // console.log('Characteristic updated in DB:', updatedChar);
          // Update local state WITH the full updated characteristic object from DB if possible
           setCharacteristics(prev => {
               const newState = prev.map(c => c.id === existingChar.id ? { ...c, ...updatedChar } : c); // Merge full update
               // console.log('Local state after update:', newState);
               return newState;
           });
        } else {
            // console.log('ACTION: No change needed, value is the same.');
        }

      } else if (newValueId !== null && newValueId !== undefined && newValueId !== '') { // Also check for non-empty string
        // Create new characteristic
        // console.log(`ACTION: Creating new characteristic for layer ${layerId}, indicator ${indicatorToUse}`);

        // === NEW: Prevent creation if Hoofdkleur ID is missing for Bijkleur/Kleursterkte ===
        if ((technicalName === 'Bijkleur' || technicalName === 'Kleursterkte') && !hoofdkleurId) {
            console.error(`Cannot create ${technicalName} because Hoofdkleur is missing for layer ${layerId}.`);
            setError(`Kan ${technicalName} niet opslaan: Selecteer eerst een Hoofdkleur voor deze laag.`);
            setSaving(false);
            return; // Stop creation
        }
        // =====================================================================================

        const newCharId = crypto.randomUUID(); // Generate UUID for the new characteristic
        // console.log('Generated new characteristic ID:', newCharId);

        const newCharData = {
          id: newCharId, // Include the generated ID
          document_id: documentId,
          link_id: layerId,
          classifiedResult: newValueId,
          // --- Use determined indicator and domain ---
          indicator: indicatorToUse,
          classifiedresult_domain: domainToUse, // Domain associated with the technicalName
          procedure: 'proc_veldwaarneming', // Example procedure
        };
        
        // === NEW: Add relatedObservation for Bijkleur/Kleursterkte ===
        if (technicalName === 'Bijkleur' || technicalName === 'Kleursterkte') {
            newCharData.relatedObservation = hoofdkleurId;
        }
        // ============================================================
        
        // console.log('New characteristic data:', newCharData);

        // Call the create function from the lookup component
        const createdCharBasic = await createCharacteristic(newCharData);
        // console.log('Characteristic created in DB (basic):', createdCharBasic);

        // Fetch the newly created characteristic with joined data for local state update
        // Select '*' should include relatedObservation
        const { data: fetchedNewChar, error: fetchError } = await supabase
          .from('document_characteristic')
          .select(`
              *,
              classified_result:document_domainvalueslookup!document_characteristic_classifiedResult_fkey(domain_value_id, description),
              indicator_info:document_domainvalueslookup!document_characteristic_indicator_fkey(domain_value_id, technical_name, description)
          `)
          .eq('id', newCharId) // Use the generated ID
          .single();

        if (fetchError) {
            // console.error("Error fetching newly created characteristic with joins:", fetchError);
            // Fallback: add basic data from the insert response to local state
             setCharacteristics(prev => {
                 const newState = [...prev, { ...createdCharBasic, classified_result: null, indicator_info: null }]; // Use basic data
                 // console.log('Local state after create (fallback):', newState);
                 return newState;
             });
        } else if (fetchedNewChar) {
             // console.log('Fetched new characteristic with joins:', fetchedNewChar);
             setCharacteristics(prev => {
                 const newState = [...prev, fetchedNewChar]; // Use fully populated data
                 // console.log('Local state after create (with joins):', newState);
                 return newState;
             });
        } else {
             // console.warn("No data returned after fetching new characteristic, using basic data.");
             setCharacteristics(prev => {
                 const newState = [...prev, { ...createdCharBasic, classified_result: null, indicator_info: null }];
                 // console.log('Local state after create (no fetch data):', newState);
                 return newState;
             });
        }

      } else {
          // console.log('ACTION: No action needed (no existing char and no new value/empty value).');
      }

    } catch (err) {
      console.error('--- handleCharacteristicUpdate ERROR ---:', err);
      setError(`Fout bij bijwerken eigenschap: ${err.message}`);
      // Optionally, re-fetch all characteristics for the document to ensure consistency on error
      // const { data: refreshedChars } = await getCharacteristicsByDocumentId(documentId); // Assuming function exists
      // if (refreshedChars) setCharacteristics(refreshedChars);
    } finally {
      setSaving(false);
      // console.log('--- handleCharacteristicUpdate END ---');
    }
  }, [allCharacteristics, setCharacteristics, technicalNameToIndicatorId, technicalNameToDomain, isReadOnly]); // Dependencies updated

  // --- NEW: Function to handle adding a Bijzonderheid pair --- 
  const handleAddCharacteristicPair = useCallback(async (layerId, documentId, pairInfo) => {
    if (isReadOnly) return; // Don't add if read-only
    // console.log('handleAddCharacteristicPair called with:', { layerId, documentId, pairInfo });
    setSaving(true);
    setError(null);

    const { naam, gradatie } = pairInfo;

    // Basic validation
    if (!naam || !naam.indicator || !naam.id || !gradatie || !gradatie.indicator || !gradatie.id || !gradatie.relatedObservation) {
        // console.error('Invalid pairInfo received in handleAddCharacteristicPair', pairInfo);
        setError('Interne fout: Ongeldige informatie voor het toevoegen van bijzonderheid.');
        setSaving(false);
        return;
    }

    let createdNaamCharId = null; // Keep track of created Naam ID for potential rollback

    try {
        // Create Naam characteristic
        const naamCharData = {
            id: naam.id,
            document_id: documentId,
            link_id: layerId,
            classifiedResult: null, // Initially null
            indicator: naam.indicator,
            classifiedresult_domain: technicalNameToDomain['BodemlaagBijzonderheden'], // Get domain
            procedure: 'proc_veldwaarneming', // Example
        };
        // console.log('Creating Naam characteristic:', naamCharData);
        await createCharacteristic(naamCharData);
        createdNaamCharId = naam.id; // Store ID after successful creation
        // console.log('Naam characteristic created.');

        // Create Gradatie characteristic
        const gradatieCharData = {
            id: gradatie.id,
            document_id: documentId,
            link_id: layerId,
            classifiedResult: null, // Initially null
            indicator: gradatie.indicator,
            classifiedresult_domain: technicalNameToDomain['BodemlaagBijzonderhedenGradatie'], // Get domain
            procedure: 'proc_veldwaarneming', // Example
            relatedObservation: gradatie.relatedObservation, // Link to Naam (which is naam.id)
        };
        // console.log('Creating Gradatie characteristic:', gradatieCharData);
        await createCharacteristic(gradatieCharData);
        // console.log('Gradatie characteristic created.');

        // Update local state - Fetch the newly created characteristics for accurate state
        // Fetch Naam with joins
        const { data: fetchedNaam, error: fetchNaamError } = await supabase
            .from('document_characteristic')
            .select(`*,
                classified_result:document_domainvalueslookup!document_characteristic_classifiedResult_fkey(domain_value_id, description),
                indicator_info:document_domainvalueslookup!document_characteristic_indicator_fkey(domain_value_id, technical_name, description)
            `)
            .eq('id', naam.id)
            .single();
        
        // Fetch Gradatie with joins
        const { data: fetchedGradatie, error: fetchGradatieError } = await supabase
            .from('document_characteristic')
            .select(`*,
                classified_result:document_domainvalueslookup!document_characteristic_classifiedResult_fkey(domain_value_id, description),
                indicator_info:document_domainvalueslookup!document_characteristic_indicator_fkey(domain_value_id, technical_name, description)
            `)
            .eq('id', gradatie.id)
            .single();

        const naamToAdd = (fetchedNaam && !fetchNaamError) 
            ? fetchedNaam 
            : { ...naamCharData, classified_result: null, indicator_info: null };
        const gradatieToAdd = (fetchedGradatie && !fetchGradatieError) 
            ? fetchedGradatie 
            : { ...gradatieCharData, classified_result: null, indicator_info: null };

        if (fetchNaamError || fetchGradatieError) {
            // console.error('Error fetching new characteristics after creation:', fetchNaamError, fetchGradatieError);
            // Still add to characteristics, but with potentially incomplete joined data
        }
        
        setCharacteristics(prev => [...prev, naamToAdd, gradatieToAdd]);
        // console.log('Local state updated with new characteristics.');

    } catch (err) {
        // console.error('--- handleAddCharacteristicPair ERROR ---:', err);
        setError(`Fout bij toevoegen bijzonderheid: ${err.message}`);
        
        // Rollback: If Naam was created but Gradatie (or subsequent steps) failed
        if (createdNaamCharId) {
            // console.log(`Attempting to rollback Naam characteristic: ${createdNaamCharId}`);
            try {
                await deleteCharacteristic(createdNaamCharId);
                // console.log(`Successfully rolled back Naam characteristic: ${createdNaamCharId}`);
                // Also remove from local state if it was added optimistically or in a partial way
                setCharacteristics(prev => prev.filter(c => c.id !== createdNaamCharId));
            } catch (rollbackError) {
                // console.error(`Failed to rollback Naam characteristic ${createdNaamCharId}:`, rollbackError);
                setError(prevError => `${prevError}\nWaarschuwing: Kon deel van bijzonderheid niet verwijderen na fout.`);
            }
        }
        // Maybe remove optimistic update from LayerDetails if needed, or refresh here
    } finally {
        setSaving(false);
    }

  }, [setCharacteristics, technicalNameToDomain, isReadOnly]); // Dependencies

  // --- NEW: Function to handle deleting a Bijzonderheid pair --- 
  const handleDeleteCharacteristicPair = useCallback(async (naamCharId, gradatieCharId) => {
    if (isReadOnly) return; // Don't delete if read-only
    console.log('handleDeleteCharacteristicPair called with:', { naamCharId, gradatieCharId });
    setSaving(true);
    setError(null);

    if (!naamCharId) { // Need at least the naam ID
        console.error('Naam characteristic ID is required for deletion.');
        setError('Interne fout: Kan bijzonderheid niet verwijderen zonder ID.');
        setSaving(false);
        return;
    }

    try {
        // Delete Naam characteristic
        console.log(`Deleting Naam characteristic: ${naamCharId}`);
        await deleteCharacteristic(naamCharId);
        console.log('Naam characteristic deleted.');

        // Delete Gradatie characteristic if ID exists
        if (gradatieCharId) {
            console.log(`Deleting Gradatie characteristic: ${gradatieCharId}`);
            await deleteCharacteristic(gradatieCharId);
            console.log('Gradatie characteristic deleted.');
        }

        // Update local state
        setCharacteristics(prev => prev.filter(c => c.id !== naamCharId && c.id !== gradatieCharId));
        console.log('Local state updated after deletion.');

    } catch (err) {
        console.error('--- handleDeleteCharacteristicPair ERROR ---:', err);
        setError(`Fout bij verwijderen bijzonderheid: ${err.message}`);
        // Potentially re-fetch characteristics on error for consistency
    } finally {
        setSaving(false);
    }
  }, [setCharacteristics, isReadOnly]); // Dependencies

  // Adjust combined loading and error states *after* all hooks are called
  const combinedLoading = loading || domainValuesLoading || characteristicsLoading || lengthUnitsLoading || referentieVlakLoading;
  const combinedError = error || domainValuesError || characteristicsError || lengthUnitsError; // Ensure this is the single, correct definition

  // DEBUG LOGGING START
  // console.log(`LayerInfo Rendering: meterUnitId=${meterUnitId}, defaultConditionId=${defaultConditionId}, combinedLoading=${combinedLoading}`);
  // DEBUG LOGGING END

  // --- EARLY RETURN BASED ON LOADING STATE ---
  // Loading state check uses combined state
  if (combinedLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  // Simplified return statement using LayerListItem
  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h6" sx={{ mb: 2 }}>Lagen</Typography>

      {/* Display combined error */} 
      {combinedError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {combinedError}
        </Alert>
      )}

      {/* --- NEW: Create Layer Dialog --- */}
      <Dialog 
        open={isCreateLayerDialogOpen} 
        onClose={() => setIsCreateLayerDialogOpen(false)} 
        maxWidth="sm" // Already sm
        PaperComponent={DraggableDialog} // Use DraggableDialog
        aria-labelledby="draggable-dialog-title" // Add aria-labelledby
        // Keep transparent backdrop
        slotProps={{
          backdrop: {
            sx: {
              backgroundColor: 'transparent',
            },
          },
        }}
        PaperProps={{ // Optional: Set initial position/size if needed
          sx: { 
            minWidth: '500px', // Ensure a minimum width
          }
        }}
      >
        {/* Modify DialogTitle for dragging */}
        <DialogTitle 
          style={{ cursor: 'move' }} 
          id="draggable-dialog-title"
          sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
        >
          <DragIndicatorIcon />
          Nieuwe Laag Toevoegen
        </DialogTitle>
        <DialogContent>
          {/* Render fields for newLayerData */}
          {newLayerData && (
            <Box component="form" noValidate autoComplete="off">
              {/* Upper depth fields */}
              <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField
                  sx={{ flex: '0 0 30%' }}
                  label="Bovendiepte"
                  margin="normal"
                  type="number"
                  value={newLayerData.upperdepth ?? ''}
                  onChange={(e) => handleNewLayerChange('upperdepth', e.target.value)}
                  inputProps={{ step: "0.01" }}
                  required // Mark as required visually
                  autoFocus // Focus on the first field
                />
                <Box sx={{ flex: '0 0 70%' }}>
                  <AutocompleteField
                    label="Eenheid"
                    value={newLayerData.upperdepth_uom}
                    onChange={(newValue) => handleNewLayerChange('upperdepth_uom', newValue)}
                    options={lengthUnitsData?.['Eenheid'] || []}
                    loading={lengthUnitsLoading}
                    keyPrefix={`new-upper-uom`}
                    isReadOnly={isReadOnly}
                  />
                </Box>
              </Box>
              <AutocompleteField
                label="Bovendiepte conditie"
                value={newLayerData.upperdepth_condition}
                onChange={(newValue) => handleNewLayerChange('upperdepth_condition', newValue)}
                options={referentieVlakData?.['Hoedanigheid'] || []}
                loading={referentieVlakLoading}
                keyPrefix={`new-upper-cond`}
                isReadOnly={isReadOnly}
              />

              {/* Lower depth fields */}
              <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField
                  sx={{ flex: '0 0 30%' }}
                  label="Onderdiepte"
                  margin="normal"
                  type="number"
                  value={newLayerData.lowerdepth ?? ''}
                  onChange={(e) => handleNewLayerChange('lowerdepth', e.target.value)}
                  inputProps={{ step: "0.01" }}
                  required // Mark as required visually
                />
                <Box sx={{ flex: '0 0 70%' }}>
                  <AutocompleteField
                    label="Eenheid"
                    value={newLayerData.lowerdepth_uom}
                    onChange={(newValue) => handleNewLayerChange('lowerdepth_uom', newValue)}
                    options={lengthUnitsData?.['Eenheid'] || []}
                    loading={lengthUnitsLoading}
                    keyPrefix={`new-lower-uom`}
                    isReadOnly={isReadOnly}
                  />
                </Box>
              </Box>
              <AutocompleteField
                label="Onderdiepte conditie"
                value={newLayerData.lowerdepth_condition}
                onChange={(newValue) => handleNewLayerChange('lowerdepth_condition', newValue)}
                options={referentieVlakData?.['Hoedanigheid'] || []}
                loading={referentieVlakLoading}
                keyPrefix={`new-lower-cond`}
                isReadOnly={isReadOnly}
              />

              {/* Layer type and sampling device */}
              <AutocompleteField
                label="Type laag"
                value={newLayerData.layer_type}
                onChange={(newValue) => handleNewLayerChange('layer_type', newValue)}
                options={domainValues['BodemlaagType'] || []}
                loading={domainValuesLoading}
                keyPrefix={`new-layer-type`}
                isReadOnly={isReadOnly}
              />
              <AutocompleteField
                label="Boorapparaat"
                value={newLayerData.samplingdevice}
                onChange={(newValue) => handleNewLayerChange('samplingdevice', newValue)}
                options={domainValues['Bemonsteringsapparaat'] || []}
                loading={domainValuesLoading}
                keyPrefix={`new-sampling-device`}
                isReadOnly={isReadOnly}
              />

            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsCreateLayerDialogOpen(false)}>Annuleren</Button>
          <Button 
            onClick={handleCreateLayer} 
            variant="contained" 
            disabled={addingLayer || !newLayerData?.upperdepth || !newLayerData?.lowerdepth || isReadOnly}
            startIcon={addingLayer ? <CircularProgress size={24} /> : null}
          >
            {addingLayer ? 'Bezig met aanmaken...' : 'Aanmaken'}
          </Button>
        </DialogActions>
      </Dialog>
      {/* ------------------------------ */}

      {Object.keys(layersByBorehole).length === 0 ? (
        <Alert severity="info" sx={{ mb: 2 }}>
          Geen boringen of lagen gevonden. Voeg eerst boringen toe via het "Boringen" tabblad en vervolgens lagen hier.
        </Alert>
      ) : (
        <List sx={{ 
          width: '100%', 
          bgcolor: 'background.paper',
          borderRadius: 1,
          overflow: 'hidden',
          '& .MuiListItemButton-root:hover': {
            bgcolor: 'action.hover',
          }
        }}>
          {Object.entries(layersByBorehole).map(([boreholeId, { borehole, layers: boreholeLayers }]) => (
            <React.Fragment key={boreholeId}>
              <ListItemButton 
                onClick={() => setExpandedBorehole(expandedBorehole === boreholeId ? null : boreholeId)}
                sx={{ 
                  bgcolor: expandedBorehole === boreholeId ? 'action.selected' : 'inherit',
                  fontWeight: expandedBorehole === boreholeId ? 'bold' : 'normal',
                  // Add check for unassigned
                  ...(boreholeId === 'unassigned' && { fontStyle: 'italic' })
                }}
              >
                <GrassIcon sx={{ mr: 2, color: boreholeId === 'unassigned' ? 'text.disabled' : 'primary.main' }} />
                <ListItemText 
                  primary={borehole.name || 'Naamloos'}
                  secondary={`${boreholeLayers.length} ${boreholeLayers.length === 1 ? 'laag' : 'lagen'}`}
                />
                {/* Only show expand icon if there are layers or it's a real borehole */}
                {(boreholeLayers.length > 0 || boreholeId !== 'unassigned') && (
                    <IconButton edge="end">
                        {expandedBorehole === boreholeId ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    </IconButton>
                )}
              </ListItemButton>
              
              <Collapse in={expandedBorehole === boreholeId} timeout="auto" unmountOnExit>
                <Box sx={{ pl: 4, pr: 2 }}>
                  {boreholeLayers.length === 0 ? (
                    <Typography color="text.secondary" sx={{ py: 2 }}>
                      Geen lagen gevonden voor deze boring
                    </Typography>
                  ) : (
                    <List dense> {/* Use dense list for layers? */}
                      {boreholeLayers.map((layer) => {
                        // Filter characteristics for the current layer
                        const layerCharacteristics = allCharacteristics?.filter(c => c.link_id === layer.id) || [];
                        
                        // --- NEW: Bundle props for LayerDetails --- 
                        const layerDetailsSpecificProps = {
                            layer: layer, // Pass the specific layer object here
                            domainValues: domainValues,
                            lengthUnitsData: lengthUnitsData,
                            lengthUnitsLoading: lengthUnitsLoading,
                            referentieVlakData: referentieVlakData,
                            referentieVlakLoading: referentieVlakLoading,
                            allCharacteristics: allCharacteristics,
                            layerCharacteristics: layerCharacteristics, 
                            setCharacteristics: setCharacteristics,
                            characteristicsLoading: characteristicsLoading,
                            domainValuesLoading: domainValuesLoading,
                            loading: loading, // This is the general loading state from LayerInfo
                            saving: saving,
                            onFieldChange: handleFieldChange,
                            setError: setError,
                            documentId: document.id, // documentId is from LayerInfo's document prop
                            onCharacteristicUpdate: handleCharacteristicUpdate,
                            technicalNameToIndicatorId: technicalNameToIndicatorId,
                            onAddCharacteristicPair: handleAddCharacteristicPair,
                            onDeleteCharacteristicPair: handleDeleteCharacteristicPair,
                            isReadOnly: isReadOnly 
                            // Props not needed by LayerDetails (e.g., boreholes, availableBoreProcesses) are omitted
                        };
                        // ------------------------------------------
                        
                        return (
                          <LayerListItem
                            key={layer.id}
                            layer={layer} // LayerListItem still needs this for its own display
                            onDeleteLayer={handleDeleteLayer}
                            isReadOnly={isReadOnly} // For the delete button in LayerListItem
                            // Pass the bundled props for LayerDetails
                            layerDetailsProps={layerDetailsSpecificProps} 
                          />
                        );
                      })}
                    </List>
                  )}
                  
                  {/* Add Layer button - disable for 'unassigned' */}
                  {boreholeId !== 'unassigned' && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', my: 2 }}>
                      <Button
                        variant="outlined"
                        startIcon={<AddIcon />}
                        onClick={() => openNewLayerDialog(boreholeId)}
                        disabled={addingLayer || !meterUnitId || !defaultConditionId || isReadOnly}
                      >
                        {addingLayer ? <CircularProgress size={24} /> : 'Nieuwe laag toevoegen'}
                      </Button>
                    </Box>
                  )}
                </Box>
              </Collapse>
              
              {/* Divider between boreholes */}
              <Divider variant="middle" component="li" />
            </React.Fragment>
          ))}
        </List>
      )}

      {/* Confirmation dialog for layer deletion remains the same */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
      >
        <DialogTitle>Laag verwijderen?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Weet je zeker dat je deze laag wilt verwijderen? 
            {layerToDelete && allCharacteristics?.some(c => c.link_id === layerToDelete.id) && (
              <strong> Let op: Gekoppelde eigenschappen worden NIET automatisch verwijderd (dit gedrag moet mogelijk worden aangepast).</strong>
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Annuleren</Button>
          <Button onClick={confirmDeleteLayer} color="error" autoFocus disabled={isReadOnly}>
            Verwijderen
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
